import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { IAuditEvent, IChoicesResponse, IOrganization } from 'src/app/core/models/audit-event';

interface IOrganizationApi {
  id: number;
  display_text: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuditLogService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/v1/audit/events`;
  private choicesUrl = `${this.apiUrl}/choices/`;
  private organizationsUrl = `${environment.apiUrl}/v1/organization/administrative/organizationautocomplete/`;
  private reportTypesUrl = `${environment.apiUrl}/v2/reporting/api/report-types/`;

  /**
   * Get filter choices from API
   */
  public getChoices(): Observable<IChoicesResponse> {
    return this.http.get<IChoicesResponse>(this.choicesUrl);
  }

  /**
   * Search organizations for multiselect filter.
   * Uses server-side search via the autocomplete endpoint.
   */
  public searchOrganizations(query: string = ''): Observable<IOrganization[]> {
    const params = query
      ? `?q=${encodeURIComponent(query)}&$top=300`
      : '?$top=300';
    return this.http.get<{ results: IOrganizationApi[] }>(`${this.organizationsUrl}${params}`)
      .pipe(map(response =>
        response.results.map(org => ({
          ...org,
          name: org.display_text
        }))
      ));
  }

  /**
   * Get audit event by ID for detail view
   */
  public getAuditEvent(id: string): Observable<IAuditEvent> {
    return this.http.get<IAuditEvent>(`${this.apiUrl}(${id})/`);
  }

  /**
   * Load the report template ID for audit-log-report
   */
  public loadReportTemplateId(): Observable<number | null> {
    const url = `${this.reportTypesUrl}?$filter=key eq 'audit-log-report'`;
    return this.http.get<{ value: any[] }>(url).pipe(
      map((response) => {
        const reportType = response.value?.[0];
        return reportType?.report_template?.id ?? null;
      })
    );
  }
}
